import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Inbox, BarChart3, CreditCard, Check, X, MailPlus, UserX, MailCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIERS = ["public", "pro", "intelligence", "enterprise", "admin"];

const STATUS_STYLE = {
  pending: "!text-amber-300 !border-amber-500/40 !bg-amber-500/10",
  approved: "!text-emerald-300 !border-emerald-500/40 !bg-emerald-500/10",
  rejected: "!text-red-300 !border-red-500/40 !bg-red-500/10",
  invited: "!text-sky-300 !border-sky-500/40 !bg-sky-500/10",
};

export const DeletionRequestsPanel = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/admin/deletion-requests`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (row, status) => {
    try {
      await axios.patch(`${API}/admin/deletion-requests/${row.request_id}`, { status });
      toast.success(`${row.email}: ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const DEL_STYLE = {
    pending: "!text-amber-300 !border-amber-500/40 !bg-amber-500/10",
    completed: "!text-emerald-300 !border-emerald-500/40 !bg-emerald-500/10",
    dismissed: "!text-white/40 !border-white/15 !bg-white/5",
  };

  return (
    <div className="mt-10" data-testid="deletion-requests-panel">
      <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
        <UserX className="w-4 h-4 text-orange-400" /> Account Deletion Requests
      </h2>
      <p className="text-xs text-white/45 mb-4">Delete the account in the user list above, then mark the request completed.</p>
      <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
        {rows.length === 0 && <p className="px-5 py-4 text-sm text-white/40" data-testid="deletion-requests-empty">No deletion requests.</p>}
        {rows.map((r, i) => (
          <div key={r.request_id}>
            <div className="px-5 py-3 flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-mono">{r.email}</p>
                <p className="text-[11px] text-white/40 mt-0.5">requested {new Date(r.created_at).toLocaleString()}{r.reason ? ` · "${r.reason}"` : ""}</p>
              </div>
              <span className={`whip-pill !py-0.5 !px-2 !text-[9px] ${DEL_STYLE[r.status] || ""}`} data-testid={`deletion-status-${r.email}`}>
                {r.status.toUpperCase()}
              </span>
              {r.status === "pending" && (
                <>
                  <button onClick={() => review(r, "completed")} className="text-emerald-400/70 hover:text-emerald-300" title="Mark completed" data-testid={`deletion-complete-${r.email}`}>
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => review(r, "dismissed")} className="text-white/40 hover:text-white/70" title="Dismiss" data-testid={`deletion-dismiss-${r.email}`}>
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
            {i < rows.length - 1 && <div className="whip-divider" />}
          </div>
        ))}
      </div>
    </div>
  );
};

export const WaitlistQueue = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/waitlist`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (row, changes, label) => {
    try {
      const r = await axios.patch(`${API}/waitlist/${row.submission_id}`, changes);
      const d = r.data || {};
      if (d.account_created && d.setup_email_sent) toast.success(`${row.email}: approved, account created and a set-your-password link was emailed`);
      else if (d.setup_email_sent) toast.success(`${row.email}: a fresh setup link was emailed`);
      else if (d.setup_link) toast.warning(`${row.email}: email could not be sent. One-time setup link: ${d.setup_link}`, { duration: 30000 });
      else toast.success(`${row.email}: ${label}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div className="mt-10" data-testid="waitlist-queue">
      <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
        <Inbox className="w-4 h-4 text-orange-400" /> Early-Access Waitlist
      </h2>
      <p className="text-xs text-white/45 mb-4">Approving creates the account at the chosen tier and emails the person a single-use link to set their own password. Use the mail icon to resend a link.</p>
      <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
        {rows.length === 0 && <p className="px-5 py-4 text-sm text-white/40" data-testid="waitlist-empty">No requests yet.</p>}
        {rows.map((r, i) => (
          <div key={r.submission_id}>
            <div className="px-5 py-3.5">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-sm">{r.name}
                    <span className="text-white/40 font-normal font-mono text-xs ml-2">{r.email}</span>
                  </p>
                  <p className="text-[11px] text-white/45 mt-0.5">
                    {r.audience}{r.organization ? ` · ${r.organization}` : ""}{r.role_title ? ` · ${r.role_title}` : ""}
                  </p>
                  {r.intended_use && <p className="text-[11px] text-white/35 mt-1 italic line-clamp-2">"{r.intended_use}"</p>}
                </div>
                <span className={`whip-pill !py-0.5 !px-2 !text-[9px] ${STATUS_STYLE[r.status] || ""}`} data-testid={`waitlist-status-${r.email}`}>
                  {r.status.toUpperCase()}
                </span>
                <select
                  value={r.intended_tier || ""}
                  onChange={(e) => review(r, { intended_tier: e.target.value }, `intended tier → ${e.target.value}`)}
                  className="rounded-md border border-white/15 bg-white/[0.03] px-2 py-1 text-[10px] font-mono uppercase text-white/60 outline-none"
                  data-testid={`waitlist-tier-${r.email}`}
                >
                  <option value="" disabled className="bg-[#12121a]">TIER…</option>
                  {TIERS.map((t) => <option key={t} value={t} className="bg-[#12121a]">{t.toUpperCase()}</option>)}
                </select>
                <button onClick={() => review(r, { status: "approved" }, "approved")}
                  className="text-emerald-400/70 hover:text-emerald-300" title="Approve" data-testid={`waitlist-approve-${r.email}`}>
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => review(r, { status: "rejected" }, "rejected")}
                  className="text-red-400/60 hover:text-red-300" title="Reject" data-testid={`waitlist-reject-${r.email}`}>
                  <X className="w-4 h-4" />
                </button>
                <button onClick={() => review(r, { status: "invited" }, "setup link resent")}
                  className="text-sky-400/60 hover:text-sky-300" title="Resend setup link" data-testid={`waitlist-invite-${r.email}`}>
                  <MailPlus className="w-4 h-4" />
                </button>
              </div>
            </div>
            {i < rows.length - 1 && <div className="whip-divider" />}
          </div>
        ))}
      </div>
    </div>
  );
};

const EMAIL_KIND_LABEL = {
  waitlist_confirmation: "Confirmation",
  waitlist_approval: "Approval",
  invite: "Invite",
  test: "Test",
};

export const EmailLogPanel = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/emails?limit=50`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);

  return (
    <div className="mt-10" data-testid="email-log-panel">
      <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
        <MailCheck className="w-4 h-4 text-orange-400" /> Email Delivery Log
      </h2>
      <p className="text-xs text-white/45 mb-4">Every confirmation, approval, and invite sent from no-reply@whipintel.com, most recent first.</p>
      <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
        {rows.length === 0 && <p className="px-5 py-4 text-sm text-white/40" data-testid="email-log-empty">No emails sent yet.</p>}
        {rows.map((r, i) => (
          <div key={`${r.to}-${r.at}`}>
            <div className="px-5 py-3 flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-mono">{r.to}</p>
                <p className="text-[11px] text-white/40 mt-0.5">
                  {r.subject} · {new Date(r.at).toLocaleString()}
                  {r.error ? <span className="text-red-300/80"> · {r.error}</span> : null}
                </p>
              </div>
              <span className="whip-pill !py-0.5 !px-2 !text-[9px] !text-white/60 !border-white/15 !bg-white/5" data-testid={`email-kind-${i}`}>
                {EMAIL_KIND_LABEL[r.kind] || r.kind}
              </span>
              <span
                className={`whip-pill !py-0.5 !px-2 !text-[9px] ${r.status === "sent"
                  ? "!text-emerald-300 !border-emerald-500/40 !bg-emerald-500/10"
                  : "!text-red-300 !border-red-500/40 !bg-red-500/10"}`}
                data-testid={`email-status-${i}`}
              >
                {(r.status || "").toUpperCase()}
              </span>
            </div>
            {i < rows.length - 1 && <div className="whip-divider" />}
          </div>
        ))}
      </div>
    </div>
  );
};

export const AnalyticsPanel = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/analytics/summary?days=30`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  const topFeatures = useMemo(() => (data?.by_feature || []).filter((f) => f.event === "view").slice(0, 8), [data]);
  if (!data) return null;
  return (
    <div className="mt-10" data-testid="analytics-panel">
      <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-orange-400" /> Product Analytics <span className="text-xs text-white/40 font-normal">last 30 days · {data.total_events.toLocaleString()} events</span>
      </h2>
      <div className="grid gap-4 md:grid-cols-2 mt-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-2">Top features by usage</p>
          {topFeatures.map((f) => (
            <div key={f.feature_key} className="flex justify-between text-xs py-1">
              <span className="text-white/70 font-mono">{f.feature_key}</span>
              <span className="text-white/45 tabular-nums">{f.count.toLocaleString()} · {f.unique_users}u</span>
            </div>
          ))}
          {(data.by_feature || []).length === 0 && <p className="text-xs text-white/35">No events yet.</p>}
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-2">Upgrade intent, locked-feature interactions</p>
          {(data.upgrade_intent || []).slice(0, 8).map((f) => (
            <div key={f.feature_key} className="flex justify-between text-xs py-1">
              <span className="text-orange-300/80 font-mono">{f.feature_key}</span>
              <span className="text-white/45 tabular-nums">{f.count.toLocaleString()} · {f.unique_users}u</span>
            </div>
          ))}
          {(data.upgrade_intent || []).length === 0 && <p className="text-xs text-white/35">No locked-feature interactions yet.</p>}
          <p className="text-[10px] font-mono uppercase tracking-wider text-white/40 mt-4 mb-1.5">Events by tier</p>
          {(data.by_tier || []).map((t) => (
            <div key={t.tier} className="flex justify-between text-xs py-0.5">
              <span className="text-white/60 font-mono">{t.tier}</span>
              <span className="text-white/45 tabular-nums">{t.count.toLocaleString()} · {t.unique_users}u</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const PlansPanel = () => {
  const [plans, setPlans] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/billing/plans`).then((r) => setPlans(r.data || [])).catch(() => setPlans([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const updatePrice = async (plan, value) => {
    const price = Number(value);
    if (Number.isNaN(price) || price === plan.price_usd) return;
    try {
      await axios.patch(`${API}/billing/plans/${plan.plan_id}`, { price_usd: price });
      toast.success(`${plan.name}: $${price}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div className="mt-10" data-testid="plans-panel">
      <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
        <CreditCard className="w-4 h-4 text-orange-400" /> Plans & Pricing
      </h2>
      <p className="text-xs text-white/45 mb-4">Pricing is configuration, not code, edit freely. No payment processor is connected yet.</p>
      <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
        {plans.map((p, i) => (
          <div key={p.plan_id}>
            <div className="px-5 py-3 flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="text-white font-semibold text-sm">{p.name}</p>
                <p className="text-[11px] text-white/40 font-mono">{p.plan_id} · {p.tier} · {p.interval}{p.seat_based ? ` · ${p.included_seats || "?"} seats` : ""}{p.trial_days ? ` · ${p.trial_days}d trial` : ""}</p>
              </div>
              {p.price_usd === null ? (
                <span className="text-xs font-mono text-white/50">CUSTOM</span>
              ) : (
                <div className="flex items-center gap-1 text-sm font-mono text-white/80">
                  $
                  <input
                    type="number"
                    min="0"
                    defaultValue={p.price_usd}
                    onBlur={(e) => updatePrice(p, e.target.value)}
                    className="w-24 rounded-md border border-white/15 bg-white/[0.03] px-2 py-1 text-sm text-white outline-none focus:border-orange-500/60 tabular-nums"
                    data-testid={`plan-price-${p.plan_id}`}
                  />
                  <span className="text-[10px] text-white/40">/{p.interval === "annual" ? "yr" : "mo"}</span>
                </div>
              )}
            </div>
            {i < plans.length - 1 && <div className="whip-divider" />}
          </div>
        ))}
      </div>
    </div>
  );
};
